import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { ClientPortalNavigation } from "../ClientPortalNavigation";
import "./clientPortalEvents.scss";
import "./portalEventCard.scss";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import Input from "components/form/input";
import CustomDropdown from "components/customDropdown/CustomDropdown";
import { clientPortalUpdate } from "redux/actions/clientPortal";
import { IFacility } from "redux/reducers/models/facility";
import axios, { CancelToken } from "axios";
import { GetFacility } from "api/rpc/2024-04/customer/facility";
import { GetFacilityGuest } from "api/rpc/2024-04/guest/facility";
import { IAPIResponse, StatusCode } from "api/protocols";
import { dequeue, enqueue, showError } from "redux/actions/ui";
import { sortFacilities } from "redux/reducers/clientPortal";
import CustomDropdownOption from "components/customDropdown/CustomDropdownOption";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar } from "@fortawesome/pro-solid-svg-icons";
import Spin from "components/spin/spin";
import { Select } from "components/select/index";
import "../Home/clientPortalHome.scss";
import { ILeague } from "redux/reducers/models/league";
import { ITournament } from "redux/reducers/models/tournament";
import { IEvent } from "redux/reducers/models/event";
import { GetGuestTournament } from "api/rpc/2024-04/guest/tournament/tournament";
import { GetGuestLeague } from "api/rpc/2024-04/guest/league";
import { GetCustomerTournament } from "api/rpc/2024-04/customer/tournament";
import { GetCustomerLeague } from "api/rpc/2024-04/customer/league";
import { GetGuestHospitalityEvent } from "api/rpc/2024-04/guest/hospitalityEvent";
import { GetCustomerHospitalityEvent } from "api/rpc/2024-04/customer/hospitalityEvent";
import moment from "moment";
import { UserActive } from "api/rpc";
import { GetClient } from "api/rpc/2024-04/customer/client";
import { GetClientGuest } from "api/rpc/2024-04/guest/client";
import PortalEventCard, { TPortalEventCard, TPortalEventType } from "./PortalEventCard";
import MessageDisplay from "components/messageDisplay/MessageDisplay";

interface IParams {
  facilityShortName: string;
  clientShortName: string;
}

type TEventTypeFilter = "all" | TPortalEventType;
type TTimeframeFilter = "upcoming" | "past";

interface IState {
  searchQuery: string;
  facilitySearchQuery: string;
  searchedFacilities: Array<IFacility>;
  leagues: Array<ILeague> | null;
  tournaments: Array<ITournament> | null;
  hospitalityEvents: Array<IEvent> | null;
}

export default function ClientPortalEvents() {
  const { t } = useTranslation();
  const { facilityShortName, clientShortName } = useParams<IParams>();
  const dispatch = useAppDispatch();
  const { clientPortalStore } = useAppSelector(store => store);
  const [eventTypeFilter, setEventTypeFilter] = useState<TEventTypeFilter>("all");
  const [timeframe, setTimeframe] = useState<TTimeframeFilter>("upcoming");
  const { Option } = Select;

  const [state, setState] = useState<IState>({
    searchQuery: "",
    facilitySearchQuery: "",
    searchedFacilities: [],
    leagues: [],
    tournaments: [],
    hospitalityEvents: [],
  });

  useEffect(() => {
    if (!clientPortalStore?.client) {
      void initialize();
    }
  }, []);

  useEffect(() => {
    const source = axios.CancelToken.source();
    let timeoutId: NodeJS.Timeout = null;
    if (clientPortalStore?.client) {
      timeoutId = global.setTimeout(() => {
        void loadEvents(source.token);
      }, 500);
    }
    return () => {
      source.cancel("Cancelled");
      clearTimeout(timeoutId);
    };
  }, [
    eventTypeFilter,
    timeframe,
    clientPortalStore?.selectedFacility,
    state.searchQuery,
    clientPortalStore?.client,
  ]);

  async function initialize() {
    let currentActiveUser = !!clientPortalStore?.user;
    let selectedClient = clientPortalStore?.client;
    let selectedFacility = clientPortalStore?.selectedFacility;
    let clientRes;
    let facilityRes;
    dispatch(enqueue());
    const activeUserRes = await UserActive(false);
    if (activeUserRes?.status === StatusCode.OK) {
      currentActiveUser = true;
    }

    if (currentActiveUser) {
      clientRes = await GetClient({ short_name: clientShortName }, false);
    } else {
      clientRes = await GetClientGuest({ short_name: clientShortName }, false);
    }
    if (clientRes.status !== StatusCode.OK || clientRes?.data?.length === 0) {
      dispatch(dequeue());
      dispatch(showError(t("Error loading client")));
      return;
    }
    selectedClient = clientRes?.data[0];
    if (currentActiveUser) {
      facilityRes = await GetFacility({ client_id: selectedClient?.id, order_by: "long_name" }, false);
    } else {
      facilityRes = await GetFacilityGuest({ client_id: selectedClient?.id, order_by: "long_name" }, false);
    }

    if (facilityRes?.status !== StatusCode.OK || facilityRes?.data?.length === 0) {
      dispatch(dequeue());
      dispatch(showError(t("Error loading facilities")));
      return;
    }

    const sortedFacilities = sortFacilities(facilityRes?.data);
    selectedFacility = selectedFacility
      ? sortedFacilities?.find(facility => facility?.id === selectedFacility?.id)
      : facilityShortName
        ? sortedFacilities?.find(facility => facility?.short_name === facilityShortName)
        : sortedFacilities[0];
    if (!selectedFacility && sortedFacilities?.length > 0) {
      selectedFacility = sortedFacilities[0];
    }
    selectedClient = { ...selectedClient, facilities: sortedFacilities };
    dispatch(
      clientPortalUpdate({
        client: selectedClient,
        selectedFacility,
        user: currentActiveUser ? activeUserRes?.data : undefined,
      }),
    );
    dispatch(dequeue());
  }

  async function loadEvents(cancelToken?: CancelToken) {
    const userActive = !!clientPortalStore?.user;
    const facilityId = clientPortalStore?.selectedFacility?.id;
    const listParams = {
      facility_id: facilityId,
      search: state.searchQuery || undefined,
      timeframe,
      limit: 100,
      offset: 0,
    };

    setState(prevState => ({ ...prevState, leagues: null, tournaments: null, hospitalityEvents: null }));

    const shouldLoadTournaments = eventTypeFilter === "all" || eventTypeFilter === "tournament";
    const shouldLoadLeagues = eventTypeFilter === "all" || eventTypeFilter === "league";
    const shouldLoadHospitality =
      eventTypeFilter === "all" ||
      eventTypeFilter === "wedding" ||
      eventTypeFilter === "banquet" ||
      eventTypeFilter === "other";

    const tournamentPromise: Promise<IAPIResponse<Array<ITournament>>> = shouldLoadTournaments
      ? userActive
        ? GetCustomerTournament(listParams, false, cancelToken)
        : GetGuestTournament(listParams, false, cancelToken)
      : Promise.resolve({ status: StatusCode.OK, data: [], message: "" });

    const leaguePromise: Promise<IAPIResponse<Array<ILeague>>> = shouldLoadLeagues
      ? userActive
        ? GetCustomerLeague(listParams, false, cancelToken)
        : GetGuestLeague(listParams, false, cancelToken)
      : Promise.resolve({ status: StatusCode.OK, data: [], message: "" });

    const hospitalityPromise: Promise<IAPIResponse<Array<IEvent>>> = shouldLoadHospitality
      ? userActive
        ? GetCustomerHospitalityEvent(listParams, false, cancelToken)
        : GetGuestHospitalityEvent(listParams, false, cancelToken)
      : Promise.resolve({ status: StatusCode.OK, data: [], message: "" });

    const [tournamentRes, leagueRes, hospitalityRes] = await Promise.all([
      tournamentPromise,
      leaguePromise,
      hospitalityPromise,
    ]);

    if (cancelToken?.reason) {
      return;
    }

    if (shouldLoadTournaments && tournamentRes?.status !== StatusCode.OK) {
      dispatch(showError(t("Error getting tournaments")));
    }
    if (shouldLoadLeagues && leagueRes?.status !== StatusCode.OK) {
      dispatch(showError(t("Error getting leagues")));
    }
    if (shouldLoadHospitality && hospitalityRes?.status !== StatusCode.OK) {
      dispatch(showError(t("Error getting hospitality events")));
    }

    setState(prevState => ({
      ...prevState,
      tournaments: tournamentRes?.data ?? [],
      leagues: leagueRes?.data ?? [],
      hospitalityEvents: hospitalityRes?.data ?? [],
    }));
  }

  const facilities = useMemo(() => {
    if (state?.facilitySearchQuery) {
      return clientPortalStore?.client?.facilities?.filter(
        facility =>
          facility?.long_name.toLowerCase().includes(state?.facilitySearchQuery?.toLowerCase()) ||
          facility?.city.toLowerCase().includes(state?.facilitySearchQuery?.toLowerCase()),
      );
    } else if (clientPortalStore?.client?.facilities) {
      return clientPortalStore?.client?.facilities;
    } else {
      return [];
    }
  }, [state?.searchedFacilities, clientPortalStore?.client?.facilities, state?.facilitySearchQuery]);

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { id, value } = e.target;
    setState(prevState => ({ ...prevState, [id]: value }));
  }

  function handleFacilityChange(id: number) {
    const foundFacility = clientPortalStore?.client?.facilities?.find(facility => facility?.id === id);
    if (foundFacility) {
      dispatch(clientPortalUpdate({ selectedFacility: foundFacility }));
    }
  }

  function navigateToRegisterLink(type: "tournament" | "league", handle: string) {
    const shortName = clientPortalStore?.selectedFacility?.short_name;
    window.open(`/${type}/${shortName}/${handle}/home`);
  }

  const loadingEvents = state.tournaments === null || state.leagues === null || state.hospitalityEvents === null;

  const eventCards = useMemo(() => {
    const fallbackLogo = clientPortalStore?.selectedFacility?.logo_source;
    const cards: TPortalEventCard[] = [];

    (state.tournaments ?? []).forEach(tournament => {
      cards.push({
        id: tournament.id,
        name: tournament.name,
        type: "tournament",
        logo_source: tournament.logo_source,
        fallback_logo_source: fallbackLogo,
        date_label: formatEventDateRange(tournament.date, tournament.end_date),
        onClick:
          timeframe === "past" ? undefined : () => navigateToRegisterLink("tournament", tournament.handle),
      });
    });

    (state.leagues ?? []).forEach(league => {
      cards.push({
        id: league.id,
        name: league.name,
        type: "league",
        logo_source: league.logo_source,
        fallback_logo_source: fallbackLogo,
        date_label: formatEventDateRange(league.start_date, league.end_date),
        onClick: timeframe === "past" ? undefined : () => navigateToRegisterLink("league", league.handle),
      });
    });

    (state.hospitalityEvents ?? []).forEach(event => {
      const type = normalizeHospitalityType(event.type);
      if (eventTypeFilter !== "all" && eventTypeFilter !== type) {
        return;
      }
      cards.push({
        id: event.id,
        name: event.name,
        type,
        logo_source: event.logo_source,
        fallback_logo_source: fallbackLogo,
        date_label: formatEventDateRange(event.date, event.end_date),
      });
    });

    return cards.sort((a, b) => {
      const aDate = moment(a.date_label.split(" - ")[0], "MMMM D, YYYY");
      const bDate = moment(b.date_label.split(" - ")[0], "MMMM D, YYYY");
      if (timeframe === "past") {
        return bDate.valueOf() - aDate.valueOf();
      }
      return aDate.valueOf() - bDate.valueOf();
    });
  }, [
    state.tournaments,
    state.leagues,
    state.hospitalityEvents,
    eventTypeFilter,
    timeframe,
    clientPortalStore?.selectedFacility,
  ]);

  const selectedFacilityElement = (
    <div className="events-facility-container">
      {clientPortalStore?.client?.facilities?.length > 1 && clientPortalStore?.selectedFacility?.logo_source && (
        <div>
          <img className="events-home-course-logo" src={clientPortalStore?.selectedFacility?.logo_source} />
        </div>
      )}
      <div>
        <div className="events-home-course-name">{clientPortalStore?.selectedFacility?.long_name}</div>
        {clientPortalStore?.selectedFacility && (
          <div className="events-course-secondary-information">
            {`${clientPortalStore?.selectedFacility?.address_line_1}, ${clientPortalStore?.selectedFacility?.city}, ${clientPortalStore?.selectedFacility?.province_name}, ${clientPortalStore?.selectedFacility?.postal}`}
          </div>
        )}
        {clientPortalStore?.selectedFacility?.address_line_2 && (
          <div className="events-course-secondary-information">
            {clientPortalStore?.selectedFacility?.address_line_2}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="client-portal-events">
      <ClientPortalNavigation showNavOptions />
      <div className="events-options-container">
        {clientPortalStore?.client?.facilities?.length > 1 ? (
          <CustomDropdown
            parentElement={selectedFacilityElement}
            onChange={handleFacilityChange}
            defaultValue={clientPortalStore?.selectedFacility?.id}
            onSearch={searchString => setState(prevState => ({ ...prevState, facilitySearchQuery: searchString }))}
            searchPlacerHolder={t("Search courses...")}
          >
            {facilities ? (
              facilities?.map((facility, index) => {
                return (
                  <CustomDropdownOption value={facility.id} key={index}>
                    <div className="events-facility-option-container">
                      <img className="events-facility-option-facility-logo" src={facility?.logo_source} />
                      <p className="events-facility-option-facility-name">{facility?.long_name}</p>
                      <p className="events-facility-option-facility-address">{facility?.city}</p>
                      {facility?.favourite && (
                        <FontAwesomeIcon
                          size="1x"
                          fixedWidth
                          className="events-facility-option-favourite"
                          icon={faStar}
                        />
                      )}
                    </div>
                  </CustomDropdownOption>
                );
              })
            ) : (
              <div className="facility-spinner">
                <Spin />
              </div>
            )}
          </CustomDropdown>
        ) : (
          selectedFacilityElement
        )}
        <div className="portal-events-filters">
          <div className="portal-events-search">
            <Input
              onChange={handleInputChange}
              value={state?.searchQuery}
              id="searchQuery"
              labelHidden
              onSearchClear={() => setState(prevState => ({ ...prevState, searchQuery: "" }))}
              placeholder={t("Search")}
            />
          </div>
          <Select
            label={t("Event Type")}
            onChange={(value: TEventTypeFilter) => setEventTypeFilter(value)}
            defaultValue={eventTypeFilter}
          >
            <Option value="all">{t("All")}</Option>
            <Option value="tournament">{t("Tournament")}</Option>
            <Option value="league">{t("League")}</Option>
            <Option value="wedding">{t("Wedding")}</Option>
            <Option value="banquet">{t("Banquet")}</Option>
            <Option value="other">{t("Other")}</Option>
          </Select>
          <Select
            label={t("Timeframe")}
            onChange={(value: TTimeframeFilter) => setTimeframe(value)}
            defaultValue={timeframe}
          >
            <Option value="upcoming">{t("Upcoming")}</Option>
            <Option value="past">{t("Past")}</Option>
          </Select>
        </div>
      </div>

      {loadingEvents ? (
        <div className="portal-events-list">
          <div className="td-spinner mt-4">
            <Spin />
          </div>
        </div>
      ) : eventCards.length === 0 ? (
        <div className="portal-events-list">
          <div className="portal-events-empty">
            <MessageDisplay
              icon="calendar"
              title={t("No events found")}
              content={t("There are no events to display. Try adjusting your search or filters.")}
              centered
            />
          </div>
        </div>
      ) : (
        <div className="portal-events-list">
          <div className="portal-events-card-grid">
            {eventCards.map(card => (
              <PortalEventCard key={`${card.type}-${card.id}`} {...card} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function normalizeHospitalityType(type?: string): TPortalEventType {
  if (type === "wedding" || type === "banquet" || type === "other") {
    return type;
  }
  return "other";
}

function formatEventDateRange(start?: string, end?: string) {
  if (!start) {
    return "";
  }
  const startLabel = moment(start).format("MMMM D, YYYY");
  if (!end || end === start) {
    return startLabel;
  }
  return `${startLabel} - ${moment(end).format("MMMM D, YYYY")}`;
}

